import { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Download, Filter, RefreshCw, Inbox, Eye, CheckCircle2, AlertCircle, ExternalLink, ReceiptText, Search } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import OrderDetails from "@/components/OrderDetails";
import CustomerTrace from "@/components/CustomerTrace";
import DateRangeFilter, { presetRange, ymd } from "@/components/DateRangeFilter";
import { fmtDateTime } from "@/lib/intl";
import { getCached, setCached } from "@/lib/pageStateCache";

const PAGE_SIZE = 25;

export default function Orders() {
  const { status } = useOutletContext();
  const navigate = useNavigate();

  const cached = getCached("orders");
  const [rows, setRows] = useState(cached?.rows ?? []);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(Boolean(cached?.rows?.length));
  const [search, setSearch] = useState(cached?.search ?? "");
  const [findId, setFindId] = useState("");
  const [findBusy, setFindBusy] = useState(false);
  const [findClient, setFindClient] = useState("");
  const [findClientBusy, setFindClientBusy] = useState(false);
  const [findClientScan, setFindClientScan] = useState("10000");
  // sale_id keyed by Acuity order id (for "Already receipted" badge in list).
  const [receiptedById, setReceiptedById] = useState({});

  // Refresh receipt lookup whenever the rows change.
  useEffect(() => {
    if (!rows.length) { setReceiptedById({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const ids = rows.map((r) => String(r.id)).filter(Boolean).slice(0, 500);
        const r = await api.post("/receipts/by-source/bulk", {
          source: "order",
          source_ids: ids,
        }, { timeout: 20000 });
        if (!cancelled) setReceiptedById(r.data || {});
      } catch (e) {
        if (!cancelled) setReceiptedById({});
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  async function findOrderById() {
    const id = findId.trim();
    if (!id) {
      toast.error("Enter an Acuity order ID");
      return;
    }
    setFindBusy(true);
    try {
      const r = await api.get(`/orders/find/${encodeURIComponent(id)}`);
      setRows([r.data]);
      setHasFetched(true);
      setPage(1);
      setSearch("");
      toast.success(`Order ${id} loaded`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Could not find order ${id}`, { description: detail });
    } finally {
      setFindBusy(false);
    }
  }

  async function searchOrdersByClient() {
    const q = findClient.trim();
    if (!q) {
      toast.error("Enter a client name, email or product name");
      return;
    }
    setFindClientBusy(true);
    try {
      const r = await api.get(`/orders/search`, {
        params: { q, max_scan: Number(findClientScan) },
        timeout: 120000,
      });
      const found = r.data?.results || [];
      setRows(found);
      setHasFetched(true);
      setPage(1);
      setSearch("");
      if (found.length === 0) {
        toast.info(`No matches for "${q}" in last ${findClientScan} Acuity orders`, {
          description: "Try a larger scan window if you suspect older orders.",
        });
      } else {
        toast.success(`${found.length} order${found.length === 1 ? "" : "s"} matched "${q}"`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Search failed", { description: detail });
    } finally {
      setFindClientBusy(false);
    }
  }
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(cached?.sort ?? { key: "time", dir: "desc" });
  const [page, setPage] = useState(cached?.page ?? 1);
  const [maxResults, setMaxResults] = useState(cached?.maxResults ?? "200");
  const [preset, setPreset] = useState(cached?.preset ?? "this_month");
  const [range, setRange] = useState(() => cached?.range ?? presetRange("this_month"));
  const [minDate, maxDate] = range;
  const [selected, setSelected] = useState(null);
  const [traceEmail, setTraceEmail] = useState(null);
  const [pushingIds, setPushingIds] = useState(() => new Set());

  // Persist filters + loaded rows so navigating away & back keeps the data.
  useEffect(() => {
    setCached("orders", {
      rows, search, sort, page, maxResults, preset, range,
      ts: Date.now(),
    });
  }, [rows, search, sort, page, maxResults, preset, range]);

  async function pushToXero(orderId) {
    setPushingIds((s) => {
      const next = new Set(s);
      next.add(orderId);
      return next;
    });
    try {
      const r = await api.post(`/invoice-sync/push/${orderId}`, null, {
        timeout: 60000,
      });
      const { skipped, invoice_number } = r.data || {};
      toast.success(
        skipped
          ? `Already invoiced as ${invoice_number || "—"}`
          : `Invoice ${invoice_number || "created"} in Xero`
      );
      // Refresh the single row so the badge updates
      await fetchData();
    } catch (e) {
      toast.error("Push failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setPushingIds((s) => {
        const next = new Set(s);
        next.delete(orderId);
        return next;
      });
    }
  }

  const queryParams = useMemo(
    () => ({
      max: Number(maxResults),
      minDate: ymd(minDate),
      maxDate: ymd(maxDate),
    }),
    [maxResults, minDate, maxDate]
  );

  async function fetchData() {
    if (!status.configured) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/orders", { params: queryParams });
      setRows(r.data || []);
      setPage(1);
      setHasFetched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load orders", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  // No auto-fetch on mount — wait for the user to click Apply so we don't
  // make a slow Acuity call until they've finalised filters.

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [
        r.id,
        r.firstName,
        r.lastName,
        r.email,
        ...(r.products || []).map((p) => p.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, search]);

  const sorted = useMemo(() => {
    const data = [...filtered];
    data.sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && (sort.key === "total" || sort.key === "amountPaid")) {
        return sort.dir === "asc" ? an - bn : bn - an;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return data;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  if (!status.configured) return <NotConnected />;

  return (
    <div className="space-y-8" data-testid="orders-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Storefront
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Product orders
        </h1>
        <p className="mt-2 text-sm text-zinc-600">
          All packages, gift certificates and product orders sold via your Acuity store.
        </p>
      </div>

      <Card className="p-4 sm:p-5 rounded-lg border border-zinc-200 shadow-none" data-testid="find-order-card">
        <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Search className="h-3.5 w-3.5" /> Find a specific order
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Acuity order ID
            </Label>
            <Input
              value={findId}
              onChange={(e) => setFindId(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") findOrderById(); }}
              placeholder="e.g. 162115356"
              className="border-zinc-300 mt-1.5 font-mono-num"
              data-testid="find-order-input"
            />
          </div>
          <Button
            onClick={findOrderById}
            disabled={findBusy}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="find-order-btn"
          >
            <Search className={`h-4 w-4 mr-1.5 ${findBusy ? "animate-pulse" : ""}`} />
            {findBusy ? "Looking up…" : "Find order"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Use this to instantly load any Acuity order by its ID — works for very old orders, subscription
          renewals and recurring-membership payments that fall outside the default list window.
        </p>

        <div className="my-4 border-t border-dashed border-zinc-200" />

        <div className="mb-2 text-2xs uppercase tracking-wider text-zinc-500">
          Don't have the order ID? Search by name, email or product
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Client / product
            </Label>
            <Input
              value={findClient}
              onChange={(e) => setFindClient(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchOrdersByClient(); }}
              placeholder="e.g. Derham, shane@…, Membership"
              className="border-zinc-300 mt-1.5"
              data-testid="find-client-input"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Scan depth</Label>
            <Select value={findClientScan} onValueChange={setFindClientScan}>
              <SelectTrigger className="border-zinc-300 mt-1.5 w-36" data-testid="find-client-scan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2000">Last 2,000</SelectItem>
                <SelectItem value="5000">Last 5,000</SelectItem>
                <SelectItem value="10000">Last 10,000</SelectItem>
                <SelectItem value="20000">Last 20,000</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={searchOrdersByClient}
            disabled={findClientBusy}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="find-client-btn"
          >
            <Search className={`h-4 w-4 mr-1.5 ${findClientBusy ? "animate-pulse" : ""}`} />
            {findClientBusy ? "Scanning…" : "Search all orders"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Full-account scan — useful for surfacing every subscription/membership renewal payment for a
          specific client, regardless of date. Higher scan depth = slower but more thorough.
        </p>
      </Card>

      <Card className="p-5 sm:p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center gap-2 mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>

        <DateRangeFilter
          preset={preset}
          setPreset={setPreset}
          range={range}
          setRange={setRange}
          idPrefix="orders-range"
        />

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              Search (name, email, order #, product)
            </Label>
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="e.g. Derham, 840, shane@…"
              className="border-zinc-300 mt-1.5"
              data-testid="orders-search-input"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Fetch limit</Label>
            <Select value={maxResults} onValueChange={setMaxResults}>
              <SelectTrigger data-testid="orders-max-results" className="border-zinc-300 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["50", "100", "200", "500", "1000"].map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={fetchData}
            data-testid="orders-refresh-btn"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={async () => {
              try {
                await downloadCsv(
                  "/orders/csv",
                  { max: Number(maxResults), minDate: ymd(minDate), maxDate: ymd(maxDate) },
                  `acuity_orders_${ymd(minDate)}_${ymd(maxDate)}.csv`,
                );
              } catch (e) {
                toast.error("CSV download failed", { description: e.message });
              }
            }}
            data-testid="download-orders-csv"
          >
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button
            variant="outline"
            className="border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900"
            onClick={async () => {
              try {
                await downloadCsv(
                  "/invoice-sync/export-csv",
                  { minDate: ymd(minDate), maxDate: ymd(maxDate), exclude_synced: true },
                  `xero_invoices_${ymd(minDate)}_${ymd(maxDate)}.csv`,
                );
                toast.success("Xero CSV ready", {
                  description: "Excludes orders already pushed successfully to Xero.",
                });
              } catch (e) {
                toast.error("Xero CSV download failed", { description: e.message });
              }
            }}
            data-testid="download-xero-csv"
            title="Download a Sales Invoice CSV ready to import directly into Xero"
          >
            <Download className="h-4 w-4 mr-1.5" /> Xero CSV
          </Button>
        </div>
        <div className="mt-3 text-xs text-zinc-500 font-mono-num" data-testid="orders-row-count">
          {sorted.length} rows
        </div>
      </Card>

      {error && (
        <div
          data-testid="orders-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                {[
                  ["time", "Date"],
                  ["firstName", "Customer"],
                  ["email", "Email"],
                  ["total", "Total"],
                  ["amountPaid", "Paid"],
                  ["status", "Status"],
                  ["products", "Products"],
                ].map(([key, label]) => (
                  <TableHead
                    key={key}
                    onClick={() => toggleSort(key)}
                    data-testid={`orders-sort-${key}`}
                    className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-zinc-600"
                  >
                    {label}
                    {sort.key === key && (
                      <span className="ml-1 text-zinc-400">
                        {sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </TableHead>
                ))}
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  Xero
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600 w-20">
                  Details
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-12 text-center text-sm text-zinc-500">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-16">
                    <div className="flex flex-col items-center justify-center text-center text-zinc-500">
                      <Inbox className="h-7 w-7 mb-2 text-zinc-300" />
                      <div className="text-sm">
                        {hasFetched
                          ? "No orders found"
                          : "Set your filters above and click Apply to load orders."}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                paged.map((r) => (
                  <TableRow key={r.id} data-testid={`order-row-${r.id}`}>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.time ? fmtDateTime(r.time) : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-zinc-950 font-medium">
                        {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono-num">#{r.id}</div>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-700">{r.email || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.total ? `$${Number(r.total).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm text-zinc-900">
                      {r.amountPaid ? `$${Number(r.amountPaid).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex px-2 py-0.5 text-xs rounded border border-zinc-200 bg-zinc-50 text-zinc-700">
                        {r.status || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-700 max-w-xs truncate">
                      {(r.products || []).map((p) => `${p.name} ×${p.quantity || 1}`).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <XeroBadge
                        order={r}
                        pushing={pushingIds.has(r.id)}
                        onPush={() => pushToXero(r.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                          onClick={() => setSelected(r)}
                          data-testid={`view-order-${r.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        {receiptedById[String(r.id)] ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/receipts?regen=${encodeURIComponent(receiptedById[String(r.id)].sale_id)}`)}
                            className="inline-flex items-center gap-1.5 h-8 px-2 text-2xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 font-mono-num"
                            data-testid={`order-receipted-${r.id}`}
                            title={`Sale ${receiptedById[String(r.id)].sale_id} · ${receiptedById[String(r.id)].generated_at?.slice(0, 10)}${receiptedById[String(r.id)].emailed_to ? ` · emailed to ${receiptedById[String(r.id)].emailed_to}` : ""}`}
                          >
                            <ReceiptText className="h-3.5 w-3.5 text-emerald-700" />
                            {receiptedById[String(r.id)].sale_id}
                          </button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                            onClick={() => navigate(`/receipts?source=order&id=${r.id}`)}
                            data-testid={`receipt-order-${r.id}`}
                            title="Generate tax receipt for this order"
                          >
                            <ReceiptText className="h-3.5 w-3.5 mr-1" /> Receipt
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {sorted.length > PAGE_SIZE && (
          <div className="flex items-center justify-between p-4 border-t border-zinc-200">
            <span className="text-xs text-zinc-500 font-mono-num">
              Page {page} / {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="orders-page-prev"
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="orders-page-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <OrderDetails
        order={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onDiagnoseCustomer={(o) => {
          if (!o.email) {
            toast.error("This order has no email to diagnose");
            return;
          }
          setTraceEmail(o.email);
        }}
        onFindRelated={(o) => {
          const q = (o.email || `${o.firstName || ""} ${o.lastName || ""}`).trim();
          if (!q) {
            toast.error("This order has no email or name to search by");
            return;
          }
          setSelected(null);
          setFindClient(q);
          setFindClientScan("20000");
          // Defer the actual search slightly so the input value renders first
          setTimeout(() => {
            (async () => {
              setFindClientBusy(true);
              try {
                const r = await api.get(`/orders/search`, {
                  params: { q, max_scan: 20000 },
                  timeout: 180000,
                });
                const found = r.data?.results || [];
                setRows(found);
                setHasFetched(true);
                setPage(1);
                setSearch("");
                if (found.length === 0) {
                  toast.info(`No matches for "${q}" in last 20,000 orders`);
                } else {
                  toast.success(`${found.length} order${found.length === 1 ? "" : "s"} for ${q}`);
                }
              } catch (e) {
                toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
              } finally {
                setFindClientBusy(false);
              }
            })();
          }, 50);
        }}
      />

      <CustomerTrace
        email={traceEmail}
        open={!!traceEmail}
        onOpenChange={(o) => !o && setTraceEmail(null)}
      />
    </div>
  );
}

function XeroBadge({ order, pushing, onPush }) {
  const xero = order.xero_invoice;
  const exportedAt = order.last_csv_exported_at;
  const exportedLine = exportedAt ? (
    <div
      className="mt-0.5 text-2xs text-zinc-500 font-mono-num"
      title={`Included in a Xero CSV export at ${exportedAt}`}
      data-testid={`xero-csv-exported-${order.id}`}
    >
      CSV: {exportedAt.slice(0, 10)}
    </div>
  ) : null;

  if (xero && xero.status === "ok" && xero.invoice_id) {
    return (
      <div>
        <a
          href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${xero.invoice_id}`}
          target="_blank"
          rel="noreferrer"
          title={`Pushed to Xero · ${xero.invoice_number || ""}`}
          data-testid={`xero-badge-ok-${order.id}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors max-w-[140px]"
        >
          <CheckCircle2 className="h-3 w-3 shrink-0" />
          <span className="font-mono-num truncate">
            {xero.invoice_number || "Synced"}
          </span>
          <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" />
        </a>
        {exportedLine}
      </div>
    );
  }

  if (xero && xero.status && xero.status !== "ok") {
    return (
      <div>
        <div className="inline-flex items-center gap-1.5">
          <span
            title={xero.error || `Status: ${xero.status}`}
            data-testid={`xero-badge-err-${order.id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-800 max-w-[140px]"
          >
            <AlertCircle className="h-3 w-3 shrink-0" />
            <span className="truncate">{xero.status}</span>
          </span>
          <button
            type="button"
            onClick={onPush}
            disabled={pushing}
            data-testid={`xero-push-retry-${order.id}`}
            className="text-2xs px-1.5 py-0.5 rounded border border-zinc-200 hover:bg-zinc-100 text-zinc-700 disabled:opacity-50"
          >
            {pushing ? "…" : "Retry"}
          </button>
        </div>
        {exportedLine}
      </div>
    );
  }

  return (
    <div>
      <div className="inline-flex items-center gap-1.5">
        <span
          title="Not pushed to Xero yet"
          data-testid={`xero-badge-pending-${order.id}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          Unmatched
        </span>
        <button
          type="button"
          onClick={onPush}
          disabled={pushing}
          data-testid={`xero-push-${order.id}`}
          className="text-2xs px-1.5 py-0.5 rounded border border-zinc-200 hover:bg-zinc-100 text-zinc-700 disabled:opacity-50"
        >
          {pushing ? "…" : "Push"}
        </button>
      </div>
      {exportedLine}
    </div>
  );
}

function NotConnected() {
  return (
    <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center">
      <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
        Connect Acuity to view orders
      </h2>
      <p className="mt-2 text-sm text-zinc-600">
        Add your User ID and API Key in Settings, then return here.
      </p>
      <Link to="/settings">
        <Button className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="orders-goto-settings">
          Open Settings
        </Button>
      </Link>
    </Card>
  );
}
